import { useEffect, useState } from 'react'
import { Timer, Pause, Play, RotateCcw, X } from 'lucide-react'
import { useAppStore } from '@/store'
import { getToken } from '@/lib/auth'
import { cn } from '@/lib/utils'

const timerAPI = (path: string) =>
  fetch(`/api/dashboard${path}`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${getToken()}`, 'Content-Type': 'application/json' },
  }).then(r => r.json())

function formatElapsed(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000)
  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = totalSeconds % 60
  const pad = (n: number) => String(n).padStart(2, '0')
  return hours > 0 ? `${hours}:${pad(minutes)}:${pad(seconds)}` : `${pad(minutes)}:${pad(seconds)}`
}

// A real, running clock face -- the server tells us the elapsed time and
// status as of its own last update (lastServerUpdate), and this ticks
// forward locally every second in between so the display is smooth rather
// than only updating whenever the next tool call happens to land.
export function TimerWidget() {
  const timerWidget = useAppStore((s) => s.timerWidget)
  const closeTimerWidget = useAppStore((s) => s.closeTimerWidget)
  const setTimerWidget = useAppStore((s) => s.setTimerWidget)
  const [displayMs, setDisplayMs] = useState(timerWidget.elapsedMs)
  const [acting, setActing] = useState(false)

  useEffect(() => {
    setDisplayMs(timerWidget.elapsedMs)
    if (timerWidget.status !== 'running') return
    const interval = setInterval(() => {
      setDisplayMs((prev) => prev + 1000)
    }, 1000)
    return () => clearInterval(interval)
  }, [timerWidget.elapsedMs, timerWidget.status, timerWidget.lastServerUpdate])

  async function applyTimerAction(endpoint: string) {
    if (acting) return
    setActing(true)
    try {
      const tr = await timerAPI(endpoint)
      setTimerWidget({
        open: tr.status !== 'none',
        status: tr.status === 'started' || tr.status === 'already_running' ? 'running'
          : tr.status === 'reset' ? (tr.running ? 'running' : 'stopped')
          : (tr.status === 'running' ? 'running' : 'stopped'),
        label: tr.label ?? null,
        elapsedMs: tr.elapsed_ms ?? 0,
        lastServerUpdate: Date.now(),
      })
    } catch { /* ignore */ } finally { setActing(false) }
  }

  if (!timerWidget.open) return null

  return (
    <div
      className="fixed z-[70] rounded-2xl shadow-2xl backdrop-blur-xl border overflow-hidden
        bottom-4 right-4 w-[260px]
        max-[480px]:left-4 max-[480px]:right-4 max-[480px]:bottom-4 max-[480px]:w-auto"
      style={{
        background: 'var(--color-surface)',
        borderColor: 'rgba(255,255,255,0.12)',
      }}
    >
      <div className="flex items-center justify-between px-4 pt-3 pb-1">
        <div className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wider text-text-muted">
          <Timer className="h-3.5 w-3.5" />
          {timerWidget.label || 'Timer'}
        </div>
        <button
          onClick={closeTimerWidget}
          className="text-text-muted hover:text-text-primary transition-colors cursor-pointer p-0.5"
          aria-label="Close timer"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>

      <div className="px-4 pb-3 flex items-center justify-between gap-3">
        <div
          className={cn(
            'font-mono text-3xl tabular-nums tracking-tight',
            timerWidget.status === 'running' ? 'text-text-primary' : 'text-text-muted'
          )}
        >
          {formatElapsed(displayMs)}
        </div>
        <div className="flex items-center gap-1.5">
          <div
            className={cn(
              'h-2 w-2 rounded-full',
              timerWidget.status === 'running' ? 'bg-emerald-400 animate-pulse' : 'bg-text-muted'
            )}
            title={timerWidget.status === 'running' ? 'Running' : 'Stopped'}
          />
        </div>
      </div>

      <div className="px-4 pb-4 flex gap-2">
        <button
          onClick={() => applyTimerAction('/timer/stop')}
          disabled={acting}
          className={cn(
            'flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-medium transition-colors',
            acting
              ? 'text-text-muted cursor-not-allowed'
              : 'text-text-muted hover:text-text-primary hover:bg-surface-elevated cursor-pointer'
          )}
          aria-label={timerWidget.status === 'running' ? 'Stop timer' : 'Timer stopped'}
        >
          {timerWidget.status === 'running' ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />}
          {timerWidget.status === 'running' ? 'Stop' : 'Stopped'}
        </button>
        <button
          onClick={() => applyTimerAction('/timer/reset')}
          disabled={acting}
          className={cn(
            'flex items-center justify-center py-2 px-2.5 rounded-lg transition-colors',
            acting
              ? 'text-text-muted cursor-not-allowed'
              : 'text-text-muted hover:text-text-primary hover:bg-surface-elevated cursor-pointer'
          )}
          aria-label="Reset timer"
        >
          <RotateCcw className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  )
}
